import type { ReactElement } from "react";
import { Link, useParams } from "react-router";

import { useApiData } from "../../api/use-api-data.js";
import { memberUrls } from "../../app/member-urls.js";
import { usePageMeta } from "../../app/use-page-meta.js";

import { LoadingPage } from "./loading-page.js";
import { PausedPaymentNotice } from "./paused-payment-notice.js";
import { ContractFields, ContractNotes, type TenantContract } from "./tenant-contract-fields.js";
import { roleBadge, type TenantRole } from "./tenant-role.js";
import { tenantApi, tenantUrls } from "./urls.js";

/** 契約情報（<c>GET /api/tenants/{契約}</c>。サーバの TenantDetail と同じ欄）。所属していなければ 404。 */
export interface TenantDetail extends TenantRole {
    readonly tenantId: number;
    /** プリザンター側（/fs）のテナント ID。0 なら環境がまだ無い。 */
    readonly serviceTenantId: number;
    /** 作成日（日付だけ。サーバが作る文言）。<b>契約者にだけ届き、管理者・一般には null</b>（行ごと出さない）。 */
    readonly createdDateLabel?: string | null;
    readonly title: string;
    readonly company: string;
    readonly roleLabel: string;
    readonly currentServiceId: string;
    /** 「ユーザ管理」を出すか（契約者と管理者。サーバが決める）。 */
    readonly canManageUsers: boolean;
    /**
     * 「請求書」を出すか。ふつうは契約者、閲覧者の指定がある契約ではその人だけ
     * （販売パートナーが間に入る契約で、仕切り価格を契約者に見せない。指定するのは運営。一般の利用者のこともある）。
     */
    readonly canViewInvoice: boolean;
    /** 自分のお名前の変更（利用者の編集画面）。 */
    readonly selfEditUrl: string;
    /** 契約の中身。一般の利用者には null（金額・オプション・使用量を出さない）。 */
    readonly contract: TenantContract | null;
}

const title = "契約情報";

/** 操作のボタン。<b>出すかどうかはサーバの欄で決める</b>（画面で役割名を見て分岐しない）。 */
function Actions({ detail }: { readonly detail: TenantDetail }): ReactElement {
    const { contract, tenantId } = detail;
    return (
        <div className="flex flex-wrap gap-2" data-tenant-actions="">
            <a className="btn-primary btn-sm" href={memberUrls.service(detail.currentServiceId)}>サービスにログイン</a>
            {detail.isContractor && contract !== null
                ? (
                    <>
                        {/* 無償利用・月額以外の契約は会員が自分で変えられない（サーバが決める。代わりの案内は PlanChangeContact） */}
                        {contract.canChangePlan
                            ? <a className="btn-secondary btn-sm" href={memberUrls.changePlan(tenantId)}>プラン変更</a>
                            : null}
                        <a className="btn-secondary btn-sm" href={memberUrls.editTenant(tenantId)}>契約情報の編集</a>
                    </>
                )
                : null}
            {/* 接続元 IP の制限を契約している契約の契約者だけ（サーバが決める） */}
            {contract?.canEditIpRestriction === true
                ? <a className="btn-secondary btn-sm" href={memberUrls.ipRestriction(tenantId)}>接続を許可する IP アドレス</a>
                : null}
            {/* カード払いの契約だけ（請求書払い・無償プランには差し替えるカードが無い） */}
            {contract?.canChangeCard === true
                ? <a className="btn-secondary btn-sm" href={memberUrls.changeCard(tenantId)}>カード情報の変更</a>
                : null}
            {detail.canViewInvoice
                ? <a className="btn-secondary btn-sm" href={memberUrls.invoices(tenantId)}>請求書</a>
                : null}
            {detail.canManageUsers
                ? <a className="btn-secondary btn-sm" href={memberUrls.tenantUsers(tenantId)}>ユーザ管理</a>
                : null}
            {/* 契約者を別の方に引き継ぐ（契約者だけ）。文中のリンクでは見つけにくいのでボタンにする */}
            {detail.isContractor && contract !== null
                ? <a className="btn-secondary btn-sm" href={tenantUrls.contractor(tenantId)}>契約者の変更</a>
                : null}
            {/* 一般の利用者は利用者の一覧を開けない。自分のお名前だけはここから直す（契約者と管理者はユーザ管理から） */}
            {detail.canManageUsers
                ? null
                : <a className="btn-secondary btn-sm" href={detail.selfEditUrl}>お名前の変更</a>}
        </div>
    );
}

/** プラン変更のボタンを出さない契約（無償利用・月額以外）で、代わりに運営へのお問い合わせを案内する。 */
function PlanChangeContact(): ReactElement {
    return (
        <div className="mb-4 flex items-center justify-between gap-4 text-sm" data-plan-change-contact="">
            <p>プランの変更は運営にお問い合わせください。</p>
            <a className="btn-secondary btn-sm shrink-0" href="/Home/Contact">お問い合わせ</a>
        </div>
    );
}

function Detail({ detail }: { readonly detail: TenantDetail }): ReactElement {
    usePageMeta(title);
    const paused = detail.contract?.paymentPaused ?? null;
    return (
        <div className="page max-w-3xl" data-tenant-detail="">
            <p className="mb-4"><Link to={tenantUrls.list} className="btn-secondary btn-sm">契約一覧へ戻る</Link></p>
            {/* 題は枠の中の先頭、戻るは枠の外の上（ご契約者の変更などと同じ並び。docs/01 §6） */}
            <div className="panel">
                <h1 className="page-title mb-7">{title}</h1>

                <div className="mb-5 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <h2 className="break-words text-base font-semibold" data-tenant-title="">{detail.title}</h2>
                        <p className="page-subtitle break-words">{detail.company}</p>
                    </div>
                    <span className={`${roleBadge(detail)} shrink-0`}>{detail.roleLabel}</span>
                </div>

                <dl className="mb-6 grid grid-cols-[8rem_1fr] gap-y-2 text-sm">
                    {/* 契約 ID はポータルの番号（URL の数字）、テナント ID はプリザンター側（/fs）の番号 */}
                    <dt className="text-slate-500">契約 ID</dt>
                    <dd data-tenant-id="">{detail.tenantId}</dd>
                    <dt className="text-slate-500">テナント ID</dt>
                    <dd data-service-tenant-id="">
                        {detail.serviceTenantId > 0
                            ? detail.serviceTenantId
                            : <span className="badge-warning">環境がまだありません</span>}
                    </dd>
                    {/* 作成日はサーバが作った文言をそのまま出す（契約者だけ。届かなければ行ごと出さない） */}
                    {detail.createdDateLabel === undefined || detail.createdDateLabel === null || detail.createdDateLabel.length === 0
                        ? null
                        : (
                            <>
                                <dt className="text-slate-500">作成日</dt>
                                <dd data-created-date="">{detail.createdDateLabel}</dd>
                            </>
                        )}
                    {detail.contract === null ? null : <ContractFields contract={detail.contract} />}
                </dl>

                {detail.contract === null ? null : <ContractNotes contract={detail.contract} />}

                {/* お支払いが止まっている契約だけ（サーバが付ける）。操作のボタンの前に置き、止まっていることに気付けるようにする */}
                {paused === null ? null : <PausedPaymentNotice paused={paused} tenantId={detail.tenantId} />}

                {detail.isContractor && detail.contract !== null && !detail.contract.canChangePlan ? <PlanChangeContact /> : null}

                <Actions detail={detail} />

                {/* 解約はほかの操作と混ぜず、罫線で区切って一番下に置く（契約者だけ。区切りごと出さない） */}
                {detail.isContractor && detail.contract !== null
                    ? (
                        <div className="mt-4 flex justify-end border-t border-slate-200 pt-4" data-cancel-row="">
                            <a className="btn-danger-quiet btn-sm" href={memberUrls.cancelTenant(detail.tenantId)}>解約</a>
                        </div>
                    )
                    : null}
            </div>
        </div>
    );
}

/** 契約情報（<c>/Tenant/Detail/{契約}</c>）。契約一覧の行を押して開く。所属していなければ見つからない画面。 */
export function TenantDetailPage(): ReactElement {
    const tenantId = useParams()["tenantId"] ?? "";
    const data = useApiData<TenantDetail>(tenantApi.detail(tenantId));

    return data.state === "ready"
        ? <Detail detail={data.value} />
        : <LoadingPage title={title} data={data} />;
}
